<script setup lang="ts">
/**
 * An order saved "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า" (api/app/pos_card.py, owner 18 ต.ค. 69): the POS keeps a card payment link —
 * no POS order, no stock, not in the kitchen — the customer pays on it and the POS makes the order by itself (the tab then
 * shows "ชำระแล้ว"). Shows the amount, the link (+ copy), until when it can be paid (21:30 the day before delivery), whether the
 * customer is paying right now, and "ส่งลิงก์ให้ลูกค้า" (the summary + the link go into the composer — staff press send) ·
 * "ต่ออายุ" (a new delivery day) · "ยกเลิกลิงก์" (a second click confirms; the link can never be paid after it) ·
 * "แก้ไขออเดอร์" (the order tab's form prefilled; saving cancels the old link and makes a new one). On POS beta a staff-only
 * warning until its checkout uses Beam's test mode (`card.test_note`).
 */
import { computed, ref, watch } from 'vue'
import type { ChatOrderRef, PosOrderResult } from '../../../api/pos/types'
import { baht, clockTime, dayKey } from '../../../lib/inbox'
import {
  bangkokTime,
  cardDueText,
  cardExpiry,
  cardLinkText,
  cardTooLate,
  deliveryDateLabel,
  deliveryOf,
  menuOf,
  slotLabel,
} from '../../../lib/order'
import { useClock } from '../../../stores/clock'
import { useInbox } from '../../../stores/inbox'
import { useOrders } from '../../../stores/order'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string; order: ChatOrderRef; result?: PosOrderResult | null; saved?: boolean }>()
const orders = useOrders()
const inbox = useInbox()
const clock = useClock()

const card = computed(() => props.order.card ?? null)
const total = computed(() => props.order.total ?? 0)
const due = computed(() => cardDueText(card.value))
const busy = computed(() => orders.cardBusy[props.order.id] ?? null)
const fetched = ref<PosOrderResult | null>(null)
const quote = computed(() => props.result?.order.order ?? fetched.value?.order.order ?? null)
// an edit made another cart and link: the plan fetched for the text belongs to the old one
watch(
  () => card.value?.code,
  () => (fetched.value = null),
)

/** "Pro 7 day 21 มื้อ · 21 มื้อ · ส่ง จ. 5 ต.ค. 10:00 - 12:00" (the link's cart when the POS plan is not loaded) */
const summary = computed(() => {
  const q = quote.value
  const c = card.value?.cart
  const parts: string[] = []
  if (q) parts.push(`${q.set_menu.name} · ${q.meals} มื้อ`)
  else if (c) {
    const name = orders.catalog?.set_menus.find((m) => m.id === c.set_menu_id)?.name ?? 'แพ็ก'
    const meals = c.items.reduce((a, i) => a + (menuOf(orders.catalog, i.menu_id)?.type === 'snacks' ? 0 : i.qty), 0)
    parts.push(`${name} · ${meals} มื้อ`)
  }
  const date = card.value?.delivery_date ?? q?.delivery.date
  if (date) {
    const dhl = (card.value?.shipping ?? q?.shipping.carrier) === 'dhl'
    const slot = dhl ? 'DHL' : slotLabel(orders.catalog, card.value?.delivery_time ?? q?.delivery.time ?? null)
    parts.push(`ส่ง ${deliveryDateLabel(date)}${slot ? ` ${slot}` : ''}`)
  }
  return parts.join(' · ')
})

/** "แก้ไขแล้ว 1 ครั้ง (ลิงก์ใหม่) · ล่าสุด 14:20 น. โดย พลอย" · "ต่ออายุ 15:02 น. โดย น้ำ" */
const history = computed(() => {
  const c = card.value
  const out: string[] = []
  if (c?.edit_count) {
    out.push(`แก้ไขแล้ว ${c.edit_count} ครั้ง (ลิงก์ใหม่ทุกครั้ง)${c.edited_at ? ` · ล่าสุด ${clockTime(c.edited_at)} น.` : ''}${c.edited_by_name ? ` โดย ${c.edited_by_name}` : ''}`)
  }
  if (c?.renewed_at) out.push(`ต่ออายุ ${clockTime(c.renewed_at)} น.${c.renewed_by_name ? ` โดย ${c.renewed_by_name}` : ''}`)
  return out.join(' · ')
})

/** The customer started paying (a charge in the last 30 min): cancel / edit wait. */
const paying = computed(() => {
  const c = card.value
  if (!c?.paying_until) return null
  const until = Date.parse(c.paying_until)
  if (Number.isNaN(until) || clock.now >= until) return null
  const since = c.paying_since ? Date.parse(c.paying_since) : NaN
  return { since: Number.isNaN(since) ? '' : bangkokTime(since), until: bangkokTime(until) }
})
/** Money in, the POS order not made yet (payhub working on it — or failed: its own red LINE alert). */
const charged = computed(() => {
  const c = card.value
  if (c?.state !== 'charged') return null
  const at = c.paid_at ? Date.parse(c.paid_at) : NaN
  return { at: Number.isNaN(at) ? '' : bangkokTime(at), stuck: !Number.isNaN(at) && clock.now - at > 10 * 60000 }
})

// ------------------------------------------------ ส่งลิงก์ให้ลูกค้า · คัดลอกลิงก์
async function sendLink(): Promise<void> {
  let res = props.result ?? fetched.value
  if (!res?.order.order) {
    try {
      fetched.value = await orders.loadChatOrder(props.order.id)
      res = fetched.value
    } catch {
      /* the text goes without the pack line */
    }
  }
  const bill = res ? { ...res, chat_order: props.order } : { chat_order: props.order, order: {}, replayed: false }
  orders.fillComposer(props.convId, cardLinkText(bill, orders.catalog))
  inbox.flash(card.value?.pay_safe === false ? 'ใส่ใบสรุปยอดและลิงก์ในช่องพิมพ์แล้ว (ลิงก์ beta ห้ามให้ลูกค้าจ่ายจริง)' : 'ใส่ใบสรุปยอดและลิงก์ชำระในช่องพิมพ์แล้ว ตรวจแล้วกดส่ง')
}

async function copyLink(): Promise<void> {
  const link = card.value?.link
  if (!link) return
  try {
    await navigator.clipboard.writeText(link)
    inbox.flash('คัดลอกลิงก์แล้ว')
  } catch {
    inbox.flash('คัดลอกไม่ได้ เลือกข้อความลิงก์แล้วคัดลอกเอง')
  }
}

// ------------------------------------------------ แก้ไขออเดอร์ (the old link is cancelled when the edit is saved)
const actErr = ref<string | null>(null)
function edit(): void {
  actErr.value = null
  if (paying.value) {
    actErr.value = `ลูกค้ากำลังชำระอยู่ แก้ไขได้หลัง ${paying.value.until} น.`
    return
  }
  if (!orders.startEdit(props.convId, props.order)) actErr.value = 'ออเดอร์นี้แก้จากแชทไม่ได้ (ไม่มีข้อมูลตะกร้าเดิม) ยกเลิกลิงก์แล้วลงใหม่'
}

// ------------------------------------------------ ยกเลิกลิงก์ (a second click confirms)
const cancelArmed = ref(false)
async function cancel(): Promise<void> {
  actErr.value = null
  if (!cancelArmed.value) {
    cancelArmed.value = true
    return
  }
  try {
    await orders.cancelOrder(props.convId, props.order.id)
    cancelArmed.value = false
    inbox.flash('ยกเลิกลิงก์แล้ว ลูกค้าจ่ายผ่านลิงก์นี้ไม่ได้อีก')
  } catch (e) {
    cancelArmed.value = false
    actErr.value = e instanceof Error ? e.message : String(e)
  }
}

// ------------------------------------------------ ต่ออายุ (a new delivery day)
const renewOpen = ref(false)
const delivery = computed(() => deliveryOf(orders.catalog, clock.now))
const isDhl = computed(() => card.value?.shipping === 'dhl')
const newDate = ref('')
const newTime = ref('')
watch(renewOpen, (on) => {
  if (!on) return
  newDate.value = cardTooLate(delivery.value.default_date, clock.now) ? '' : delivery.value.default_date
  newTime.value = card.value?.delivery_time ?? delivery.value.default_time
})
const renewLate = computed(() => !!newDate.value && cardTooLate(newDate.value, clock.now))
const renewUntil = computed(() => {
  if (!newDate.value || renewLate.value) return ''
  const t = cardExpiry(newDate.value)
  return Number.isNaN(t) ? '' : `${deliveryDateLabel(dayKey(t))} ${bangkokTime(t)} น.`
})
async function renew(): Promise<void> {
  actErr.value = null
  if (!newDate.value || renewLate.value) return
  try {
    await orders.renewCard(props.convId, props.order.id, {
      delivery_date: newDate.value,
      ...(isDhl.value ? {} : { delivery_time: newTime.value || null }),
    })
    renewOpen.value = false
    inbox.flash('ต่ออายุลิงก์แล้ว ลิงก์เดิมใช้ต่อได้ ส่งวันที่ใหม่ให้ลูกค้ารู้ด้วย')
  } catch (e) {
    actErr.value = e instanceof Error ? e.message : String(e)
  }
}

async function refresh(): Promise<void> {
  actErr.value = null
  try {
    const after = await orders.refreshCard(props.convId, props.order.id)
    if (after.status === 'done') inbox.flash(`ลูกค้าชำระแล้ว ออกใบ ${after.order_code ?? ''} ใน POS`.trim())
  } catch (e) {
    actErr.value = e instanceof Error ? e.message : String(e)
  }
}
</script>

<template>
  <article class="cl" :class="{ late: due?.late, big: saved }" aria-label="ออเดอร์รอชำระด้วยลิงก์บัตร">
    <div class="cl-h">
      <span class="cl-chip"><Icon name="creditCard" />รอชำระ · บัตร</span>
      <b class="cl-amt mono">{{ baht(total) }}</b>
      <span class="grow"></span>
      <span v-if="card?.code" class="cl-code mono" title="รหัสลิงก์ชำระบัตรในหน้าขาย POS">ลิงก์ {{ card.code }}</span>
    </div>

    <p v-if="card?.test_note" class="cl-test" role="note">
      <Icon name="alertCircle" /><span>{{ card.test_note }} <em>(เห็นเฉพาะทีม)</em></span>
    </p>

    <div class="cl-sumrow">
      <p class="cl-sum">{{ summary }}</p>
      <button class="tlink ac cl-edit" :disabled="!!busy" @click="edit"><Icon name="pencil" />แก้ไขออเดอร์</button>
    </div>
    <p v-if="history" class="cl-ed">{{ history }}</p>
    <p v-if="card?.link_resend" class="cl-resend" role="status">
      <Icon name="alert" /><span>แก้ออเดอร์แล้ว ลิงก์เดิมถูกยกเลิก ส่งลิงก์ใหม่ให้ลูกค้า</span>
    </p>

    <div v-if="card?.link" class="cl-link">
      <Icon name="link" />
      <a class="cl-url mono" :href="card.link" target="_blank" rel="noopener noreferrer" :title="card.link">{{ card.link }}</a>
      <button class="tlink cl-copy" aria-label="คัดลอกลิงก์ชำระ" @click="copyLink"><Icon name="copy" />คัดลอก</button>
    </div>

    <p v-if="charged" class="cl-state" :class="{ bad: charged.stuck }" role="status">
      <Icon :name="charged.stuck ? 'alertCircle' : 'checkCircle'" /><span>{{
        charged.stuck
          ? `ลูกค้าชำระแล้ว${charged.at ? ` (${charged.at} น.)` : ''} แต่ POS ยังไม่ออกใบเกิน 10 นาที แจ้งผู้ดูแลระบบ (payhub)`
          : `ลูกค้าชำระแล้ว${charged.at ? ` (${charged.at} น.)` : ''} ระบบกำลังออกใบ`
      }}</span>
    </p>
    <p v-else-if="paying" class="cl-state" role="status">
      <span class="spin"></span><span
        >ลูกค้ากำลังชำระ{{ paying.since ? ` (เริ่ม ${paying.since} น.)` : '' }} ยกเลิกหรือแก้ไขได้หลัง {{ paying.until }} น.</span
      >
    </p>
    <p v-else-if="due" class="cl-due" :class="{ late: due.late }">
      <Icon :name="due.late ? 'alertCircle' : 'calendar'" /><span>{{ due.text }}</span>
    </p>

    <div class="cl-acts">
      <button class="btn-save" :disabled="!card?.link" @click="sendLink"><Icon name="send" />ส่งลิงก์ให้ลูกค้า</button>
      <button v-if="!renewOpen" class="btn2" :disabled="!!busy || !!charged" @click="renewOpen = true">
        <Icon name="calendar" />ต่ออายุ
      </button>
      <button class="btn2 cl-x" :class="{ armed: cancelArmed }" :disabled="!!busy || !!charged" @click="cancel">
        <span v-if="busy === 'cancel'" class="spin"></span><Icon v-else name="x" />{{
          busy === 'cancel' ? 'กำลังยกเลิก…' : cancelArmed ? 'ยืนยันยกเลิกลิงก์' : 'ยกเลิกลิงก์'
        }}
      </button>
    </div>
    <p v-if="cancelArmed" class="cl-note">
      ยกเลิกแล้วลูกค้าจ่ายผ่านลิงก์นี้ไม่ได้อีก (ไม่มีออเดอร์เกิดขึ้น) · กด "ยืนยันยกเลิกลิงก์" อีกครั้ง
      <button class="tlink" @click="cancelArmed = false">ไม่ยกเลิก</button>
    </p>

    <div v-if="renewOpen" class="cl-renew" role="group" aria-label="ต่ออายุลิงก์">
      <div class="cl-g2">
        <label class="fl"
          ><span class="fl-l">วันส่งใหม่</span
          ><span class="inp" :class="{ bad: renewLate }"
            ><input v-model="newDate" type="date" :min="delivery.date_min" :max="delivery.date_max" aria-label="วันส่งใหม่" /></span
        ></label>
        <label v-if="!isDhl" class="fl"
          ><span class="fl-l">รอบส่ง</span
          ><span class="inp"
            ><select v-model="newTime" aria-label="รอบส่งใหม่">
              <option v-for="s in delivery.slots" :key="s.time" :value="s.time">{{ s.label }}</option>
            </select></span
          ></label
        >
      </div>
      <p v-if="renewLate" class="err" role="alert">
        <Icon name="alertCircle" /><span>วันนี้เลย 21:30 ของวันก่อนส่งแล้ว ลิงก์จะหมดอายุทันที เลือกวันถัดไป</span>
      </p>
      <p v-else-if="renewUntil" class="note">ลิงก์เดิมใช้ต่อได้ ชำระได้ถึง {{ renewUntil }}</p>
      <div class="cl-acts">
        <button class="btn2" :disabled="busy === 'renew'" @click="renewOpen = false">ยกเลิก</button>
        <button class="btn-save" :disabled="!!busy || !newDate || renewLate" @click="renew">
          <span v-if="busy === 'renew'" class="spin"></span><Icon v-else name="check" />{{ busy === 'renew' ? 'กำลังต่ออายุ…' : 'ต่ออายุลิงก์' }}
        </button>
      </div>
    </div>

    <p v-if="actErr" class="err" role="alert"><Icon name="alertCircle" /><span>{{ actErr }}</span></p>
    <p class="note cl-foot">
      ลูกค้าจ่ายผ่านลิงก์แล้ว POS ออกใบให้เอง (ตัดสต๊อก เข้าครัว) แชทขึ้นว่าชำระแล้วเอง ·
      <button class="tlink" :disabled="!!busy" @click="refresh">
        <span v-if="busy === 'refresh'" class="spin"></span><Icon v-else name="refresh" />ตรวจสถานะตอนนี้
      </button>
    </p>
  </article>
</template>

<style scoped>
.cl {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #c9d8f0;
  border-radius: 12px;
  background: #f6f9ff;
  text-align: left;
}
.cl.late {
  border-color: #efb0a9;
  background: #fff6f5;
}
.cl-h {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cl-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  padding: 1px 8px;
  border-radius: 6px;
  background: #e2ebfb;
  color: #1d4385;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
}
.cl-chip :deep(.ic) {
  width: 13px;
  height: 13px;
}
.cl-amt {
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
}
.cl.big .cl-amt {
  font-size: 18px;
}
.cl-code {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
/* staff only: POS beta still charges through Beam's live account */
.cl-test {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #efb0a9;
  border-radius: 8px;
  background: #fff1ef;
  color: #a61b12;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
}
.cl-test em {
  font-style: normal;
  font-weight: 400;
}
.cl-test :deep(.ic),
.cl-resend :deep(.ic),
.cl-due :deep(.ic),
.cl-state :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 2.5px;
}
.cl-sumrow {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.cl-sum {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: #3e4d46;
}
.cl-edit {
  flex: none;
  white-space: nowrap;
}
.cl-ed {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.cl-resend {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #efb0a9;
  border-radius: 8px;
  background: #fff1ef;
  color: #a61b12;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
}
.cl-link {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 6px 8px 6px 10px;
  border: 1px solid #d5e0f3;
  border-radius: 8px;
  background: #fff;
}
.cl-link > :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  color: var(--mut);
}
.cl-url {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  line-height: 19px;
  color: #1d4385;
  text-decoration: none;
}
.cl-url:hover {
  text-decoration: underline;
}
.cl-copy {
  flex: none;
  white-space: nowrap;
}
.cl-due,
.cl-state {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: #33476b;
}
.cl-due.late,
.cl-state.bad {
  color: #a61b12;
  font-weight: 600;
}
.cl-state .spin {
  flex: none;
  margin-top: 3px;
}
.cl-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cl-acts .btn2,
.cl-acts .btn-save {
  height: 38px;
  font-size: 13px;
}
.cl-acts .btn-save {
  flex: 1 1 auto;
}
.cl-x.armed {
  border-color: #d9534f;
  color: #a61b12;
  font-weight: 600;
}
.cl-note {
  margin: -2px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: #6b3a35;
}
.cl-renew {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px dashed #c9d8f0;
}
.cl-g2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}
.cl-foot {
  margin: 0;
}
.cl-foot .tlink {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
